import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import type { ClientListRow, ClientPortalState } from "@/types";
import { Button, Spinner } from "@/components/ui";
import { formatDateShort } from "@/lib/format";
import { useClientsList } from "./useClientsList";
import { ClientCreateDrawer } from "./ClientCreateDrawer";
import "./clients.css";

const PER_PAGE = 50;

type FilterMode = "all" | "companies" | "individuals" | "incomplete" | "has_apps" | "app_active" | "app_pending" | "app_none";
type SortField = "display_name" | "email" | "portal_state" | "job_count" | "open_task_count" | "open_application_count";

/** "Invited, not signed in" and "No app access" are deliberately SEPARATE chips:
 *  a client whose invite is sitting unopened is a follow-up, a client who was
 *  never invited is not, and a single "no sign-in" filter would blur the two. */
const FILTERS: { value: FilterMode; label: string }[] = [
  { value: "all", label: "All" },
  { value: "companies", label: "Companies" },
  { value: "individuals", label: "Individuals" },
  { value: "incomplete", label: "Incomplete" },
  { value: "has_apps", label: "Has open applications" },
  { value: "app_active", label: "Active in app" },
  { value: "app_pending", label: "Invited, not signed in" },
  { value: "app_none", label: "No app access" },
];

const FILTER_MODES = FILTERS.map((f) => f.value);
const SORT_FIELDS: SortField[] = ["display_name", "email", "portal_state", "job_count", "open_task_count", "open_application_count"];

/** Sort order for the app-access column: most engaged first, so ascending
 *  reads Active → Invited → Access off → No access. */
const PORTAL_RANK: Record<ClientPortalState, number> = { active: 0, pending: 1, disabled: 2, none: 3 };

/** The natural starting direction when a column is freshly selected (text A–Z,
 *  app access most-engaged-first, counts high-first since "who has the most"
 *  is the usual question). */
function naturalDirection(field: SortField): "asc" | "desc" {
  return field === "display_name" || field === "email" || field === "portal_state" ? "asc" : "desc";
}

function sortValue(row: ClientListRow, field: SortField): number | string {
  return field === "portal_state" ? PORTAL_RANK[row.portal_state] : row[field];
}

function passesFilter(row: ClientListRow, mode: FilterMode): boolean {
  switch (mode) {
    case "companies":
      return row.is_company;
    case "individuals":
      return !row.is_company;
    case "incomplete":
      return row.is_incomplete;
    case "has_apps":
      return row.open_application_count > 0;
    case "app_active":
      return row.portal_state === "active";
    case "app_pending":
      return row.portal_state === "pending";
    case "app_none":
      return row.portal_state === "none";
    default:
      return true;
  }
}

/** The active search + filter set ("what subset am I working on") persists for
 *  the SESSION only (sessionStorage), so clicking into a client and coming back
 *  keeps your search/filter — but a fresh app open starts clean. Mirrors the
 *  jobs list. The sort (below) is a separate, long-lived view choice and so
 *  persists across sessions (localStorage). */
const FILTER_PREF_KEY = "arkitectly.clientsList.filters.v1";
const SORT_PREF_KEY = "arkitectly.clientsList.sort.v1";

interface StoredFilters {
  q: string;
  filter: FilterMode;
  includeArchived: boolean;
}

const EMPTY_FILTERS: StoredFilters = { q: "", filter: "all", includeArchived: false };

function loadFilterPref(): StoredFilters {
  try {
    const raw = sessionStorage.getItem(FILTER_PREF_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<Record<keyof StoredFilters, unknown>>;
      const filter = FILTER_MODES.includes(p.filter as FilterMode) ? (p.filter as FilterMode) : "all";
      return {
        q: typeof p.q === "string" ? p.q : "",
        filter,
        includeArchived: Boolean(p.includeArchived),
      };
    }
  } catch {
    /* malformed JSON or storage unavailable — fall through to no filters */
  }
  return EMPTY_FILTERS;
}

function saveFilterPref(f: StoredFilters): void {
  try {
    sessionStorage.setItem(FILTER_PREF_KEY, JSON.stringify(f));
  } catch {
    /* storage unavailable — filters stay in memory for this visit only */
  }
}

function loadSortPref(): { field: SortField; direction: "asc" | "desc" } {
  try {
    const raw = localStorage.getItem(SORT_PREF_KEY);
    if (raw) {
      const p = JSON.parse(raw) as { field?: unknown; direction?: unknown };
      const field = SORT_FIELDS.includes(p.field as SortField) ? (p.field as SortField) : undefined;
      const direction = p.direction === "asc" || p.direction === "desc" ? p.direction : undefined;
      if (field && direction) return { field, direction };
    }
  } catch {
    /* malformed JSON or storage unavailable — fall through to defaults */
  }
  return { field: "display_name", direction: "asc" };
}

function saveSortPref(field: SortField, direction: "asc" | "desc"): void {
  try {
    localStorage.setItem(SORT_PREF_KEY, JSON.stringify({ field, direction }));
  } catch {
    /* storage unavailable — preference stays in memory for this visit only */
  }
}

/** Jobs-adjacent Clients section — a single GET /clients/list (all rows) drives a
 *  searchable, filterable, sortable, paginated table; each name links to the
 *  client hub at /staff/clients/:id. Search/filter/sort/pagination are all
 *  client-side over the in-memory set. The fetch lives in useClientsList: a
 *  module-level SWR cache + in-flight dedupe, so client → back → list paints the
 *  cached rows instantly and revalidates in the background ("Refreshing…"). The
 *  search + filter persist for the session, the sort across sessions. "New
 *  client" opens a standalone create drawer; on success the list revalidates. */
export function ClientsList() {
  const { rows, total, loading, refreshing, error, reload } = useClientsList();

  // Restore this session's search + filter so client → back → list keeps the
  // subset you were working on. Parsed once (lazy init).
  const [restored] = useState(loadFilterPref);
  const [qInput, setQInput] = useState(restored.q);
  const [q, setQ] = useState(restored.q);
  const [filter, setFilter] = useState<FilterMode>(restored.filter);
  const [includeArchived, setIncludeArchived] = useState(restored.includeArchived);
  const [sortField, setSortField] = useState<SortField>(() => loadSortPref().field);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(() => loadSortPref().direction);
  const [page, setPage] = useState(1);
  const [showNew, setShowNew] = useState(false);

  // The error string is owned by the fetch hook; track the one the user has
  // clicked-to-dismiss so a fresh/different error still surfaces.
  const [dismissedError, setDismissedError] = useState<string | undefined>();
  const visibleError = error && error !== dismissedError ? error : undefined;

  const location = useLocation();
  const navigate = useNavigate();

  // The header's "New client" routes here with { create: true } — open the
  // drawer and strip the flag so a refresh/back doesn't re-open it.
  useEffect(() => {
    if ((location.state as { create?: boolean } | null)?.create) {
      setShowNew(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  // Debounce the search box; reset to page 1 on every applied query change.
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(qInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [qInput]);

  // Persist the sort (long-lived view choice) and the search + filter set (this
  // session's working subset). We store the applied `q`, not the raw keystroke,
  // so the search box and the query restore in lockstep.
  useEffect(() => {
    saveSortPref(sortField, sortDirection);
  }, [sortField, sortDirection]);

  useEffect(() => {
    saveFilterPref({ q, filter, includeArchived });
  }, [q, filter, includeArchived]);

  const filtered = useMemo(() => {
    const needle = q.toLowerCase();
    const out = rows.filter((r) => {
      if (!includeArchived && r.is_archived) return false;
      if (!passesFilter(r, filter)) return false;
      if (needle) {
        const haystack = `${r.display_name} ${r.email} ${r.phone}`.toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
    const dir = sortDirection === "asc" ? 1 : -1;
    out.sort((a, b) => {
      const av = sortValue(a, sortField);
      const bv = sortValue(b, sortField);
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv), "en-GB", { sensitivity: "base" }) * dir;
    });
    return out;
  }, [rows, includeArchived, filter, q, sortField, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  function applySort(field: SortField) {
    if (field === sortField) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection(naturalDirection(field));
    }
    setPage(1);
  }

  function selectFilter(mode: FilterMode) {
    setFilter(mode);
    setPage(1);
  }

  return (
    <section className="clients-list">
      <header className="clients-list__head">
        <div>
          <h1>Clients</h1>
          <p className="clients-list__count">
            {total} client{total === 1 ? "" : "s"}
            {filtered.length !== total ? <span className="clients-list__sub"> · {filtered.length} shown</span> : null}
            {refreshing ? <span className="jobs-list__refreshing"> · Refreshing…</span> : null}
          </p>
        </div>
      </header>

      <div className="clients-list__filters">
        <input
          className="leads__search clients-list__search"
          type="search"
          placeholder="Search name, email, phone…"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
        />
        <label className="clients-list__toggle">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => {
              setIncludeArchived(e.target.checked);
              setPage(1);
            }}
          />
          Include archived
        </label>
      </div>

      <div className="clients-list__chips">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            className={`jobs-stage-chip${filter === f.value ? " jobs-stage-chip--on" : ""}`}
            aria-pressed={filter === f.value}
            onClick={() => selectFilter(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {visibleError ? (
        <p className="leads__error" role="alert" onClick={() => setDismissedError(error)}>{visibleError}</p>
      ) : null}

      {loading ? (
        <div className="clients-list__loading"><Spinner label="Loading clients…" /></div>
      ) : filtered.length === 0 ? (
        <p className="clients-list__empty">
          {total === 0 ? "No clients yet." : "No clients match these filters."}
        </p>
      ) : (
        <div className="jobs-table" role="table" aria-label="Clients">
          <div className="jobs-table__head clients-table__cols" role="row">
            <SortHeader label="Name" field="display_name" sortField={sortField} dir={sortDirection} onSort={applySort} />
            <SortHeader label="Email" field="email" sortField={sortField} dir={sortDirection} onSort={applySort} />
            <span role="columnheader">Phone</span>
            <SortHeader label="Client app" field="portal_state" sortField={sortField} dir={sortDirection} onSort={applySort} />
            <SortHeader label="Jobs" field="job_count" sortField={sortField} dir={sortDirection} onSort={applySort} />
            <SortHeader label="Open tasks" field="open_task_count" sortField={sortField} dir={sortDirection} onSort={applySort} />
            <SortHeader label="Open apps" field="open_application_count" sortField={sortField} dir={sortDirection} onSort={applySort} />
          </div>
          {pageRows.map((row) => (
            <div key={row.id} className="jobs-table__row clients-table__cols clients-row" role="row">
              <span role="cell" data-label="Name" className="clients-table__name">
                {/* The row's primary action: a stretched link (::after covers the
                    whole row) so any non-interactive area opens the client hub. */}
                <Link to={`/staff/clients/${row.id}`} className="clients-table__display clients-row__link">
                  {row.display_name}
                </Link>
                <span className="clients-table__badges">
                  {row.is_company ? <span className="clients-badge clients-badge--company">Company</span> : null}
                  {row.is_incomplete ? <span className="clients-badge clients-badge--warn" title="Missing email or not in Xero">Incomplete</span> : null}
                  {!row.xero_synced ? <span className="clients-badge clients-badge--muted" title="No Xero contact yet">Not in Xero</span> : null}
                  {row.is_archived ? <span className="clients-badge clients-badge--muted">Archived</span> : null}
                </span>
              </span>
              <span role="cell" data-label="Email" className="clients-table__email">
                {row.email ? <a href={`mailto:${row.email}`} className="clients-row__action">{row.email}</a> : "—"}
              </span>
              <span role="cell" data-label="Phone">
                {row.phone ? <a href={`tel:${row.phone}`} className="clients-row__action">{row.phone}</a> : "—"}
              </span>
              <span role="cell" data-label="Client app">
                <PortalBadge state={row.portal_state} lastLoginAt={row.portal_last_login_at} />
              </span>
              <span role="cell" data-label="Jobs" className="numeric">{row.job_count}</span>
              <span role="cell" data-label="Open tasks" className="numeric">{row.open_task_count}</span>
              <span role="cell" data-label="Open apps" className="numeric">{row.open_application_count}</span>
            </div>
          ))}
        </div>
      )}

      {!loading && totalPages > 1 ? (
        <div className="jobs-pager">
          <Button variant="ghost" size="sm" disabled={safePage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            ← Previous
          </Button>
          <span className="jobs-pager__status">Page {safePage} of {totalPages}</span>
          <Button variant="ghost" size="sm" disabled={safePage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
            Next →
          </Button>
        </div>
      ) : null}

      {showNew ? (
        <ClientCreateDrawer
          onClose={() => setShowNew(false)}
          onCreated={() => {
            setShowNew(false);
            reload();
          }}
        />
      ) : null}
    </section>
  );
}

/** The app-access cell, the same vocabulary as the client hub's "Client app"
 *  card. "none" renders as a dash rather than a badge: on a list where most
 *  clients have no access, that keeps the two states worth a look — Invited
 *  (never signed in) and Access off — visible instead of lost among
 *  identical-looking badges. */
function PortalBadge({ state, lastLoginAt }: { state: ClientPortalState; lastLoginAt: number }) {
  switch (state) {
    case "active":
      return (
        <span
          className="clients-badge clients-badge--ok"
          title={lastLoginAt > 0 ? `Last signed in ${formatDateShort(lastLoginAt)}` : undefined}
        >
          Active
        </span>
      );
    case "pending":
      return <span className="clients-badge clients-badge--warn" title="Invited to the app but has never signed in">Invited</span>;
    case "disabled":
      return <span className="clients-badge clients-badge--muted" title="App access switched off">Access off</span>;
    default:
      return <span className="clients-table__none" title="No app access">—</span>;
  }
}

function SortHeader({
  label,
  field,
  sortField,
  dir,
  onSort,
}: {
  label: string;
  field: SortField;
  sortField: SortField;
  dir: "asc" | "desc";
  onSort: (field: SortField) => void;
}) {
  const active = sortField === field;
  return (
    <button
      type="button"
      role="columnheader"
      className={`jobs-table__sort${active ? " jobs-table__sort--active" : ""}`}
      aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
      onClick={() => onSort(field)}
    >
      {label}
      <span className="jobs-table__sort-arrow" aria-hidden="true">{active ? (dir === "asc" ? "↑" : "↓") : "↕"}</span>
    </button>
  );
}
